/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useApiKeyData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading API Key data for edit/duplicate mode and tenants.
    Now includes domain loading for domain scope functionality.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added domain scope)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import { fetchApiKeyById, fetchDomainsByTenant, DomainOption } from '../api';
import { ApiKeyFormState } from './useApiKeyForm';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
}

export function useApiKeyData(
  mode: 'create' | 'edit',
  id: string | undefined,
  duplicateId: string | null,
  setFormData: (data: ApiKeyFormState) => void,
  setOriginalData: (data: ApiKeyFormState) => void,
  setGeneratedApiKey: (key: string | null) => void,
) {
  const { user } = useAuthStore();
  const { showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug ?? t('apiKeys.defaultTenant');
  const userId = user?.id;

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loading, setLoading] = useState(false);

  // Domain state for domain scope functionality
  const [domains, setDomains] = useState<DomainOption[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);

  const apiKeyLoadedRef = useRef<string | null>(null);
  const isLoadingTenantsRef = useRef(false);
  const tenantsLoadedRef = useRef(false);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);

  const loadTenants = useCallback(async () => {
    if (isLoadingTenantsRef.current || tenantsLoadedRef.current) {
      return;
    }

    if (!tenantSlug || !userId) {
      return;
    }

    try {
      isLoadingTenantsRef.current = true;
      setLoadingTenants(true);

      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.TENANTS), {
        headers: getApiHeaders(tenantSlug, userId),
      });

      if (!response.ok) {
        throw new Error(t('apiKeys.errors.tenantsLoad'));
      }

      const result = await response.json();
      setTenants(Array.isArray(result.data) ? result.data : []);
      tenantsLoadedRef.current = true;
    } catch (error) {
      setTenants([]);
      showError(error instanceof Error ? error.message : t('apiKeys.errors.tenantsLoad'));
    } finally {
      setLoadingTenants(false);
      isLoadingTenantsRef.current = false;
    }
  }, [tenantSlug, userId, showError, t]);

  // Load domains for a specific tenant
  const loadDomains = useCallback(async (selectedTenantId: number | null) => {
    if (!selectedTenantId) {
      setDomains([]);
      return;
    }

    try {
      setLoadingDomains(true);
      const fetchedDomains = await fetchDomainsByTenant(selectedTenantId, tenantSlug, userId);
      setDomains(fetchedDomains);
    } catch (error) {
      setDomains([]);
    } finally {
      setLoadingDomains(false);
    }
  }, [tenantSlug, userId]);

  useEffect(() => {
    if (tenantSlug && userId && !tenantsLoadedRef.current) {
      loadTenants();
    }
  }, [tenantSlug, userId, loadTenants]);

  const loadApiKey = useCallback(async () => {
    const loadId = mode === 'edit' ? id : duplicateId;
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';

    if (!loadId || loadId === 'new') {
      // Reset the flag if we were previously loading an API key OR if ID changed to 'new'
      if (idChangedToNew || (apiKeyLoadedRef.current !== null && apiKeyLoadedRef.current !== 'create-initialized')) {
        formResetRef.current = false;
      }

      // Reset form when navigating to new (either initial load or after save)
      if (!formResetRef.current || idChangedToNew) {
        formResetRef.current = true;
        apiKeyLoadedRef.current = 'create-initialized';
        const initialState: ApiKeyFormState = {
          name: '',
          description: '',
          tenantId: null,
          domainId: null,
          permissions: {},
          isActive: true,
          expiresAt: '',
        };
        setFormData(initialState);
        setOriginalData(initialState);
        setGeneratedApiKey(null);
        setDomains([]);
      }

      lastIdRef.current = currentId;
      return;
    }

    // Reset the form reset flag when loading an actual API key
    formResetRef.current = false;

    if (apiKeyLoadedRef.current === loadId) {
      lastIdRef.current = currentId;
      return;
    }
    apiKeyLoadedRef.current = loadId;
    lastIdRef.current = currentId;

    try {
      setLoading(true);
      const apiKey = await fetchApiKeyById(loadId, tenantSlug, userId);

      const loadedData: ApiKeyFormState = {
        name: mode === 'edit' ? apiKey.name : `${apiKey.name}_copy`,
        description: apiKey.description || '',
        tenantId: apiKey.tenantId ?? apiKey.tenant?.id ?? null,
        domainId: apiKey.domainId ?? null,
        permissions: apiKey.permissions || {},
        isActive: apiKey.isActive,
        expiresAt: (() => {
          if (!apiKey.expiresAt || apiKey.expiresAt === 'null' || apiKey.expiresAt === null) {
            return '';
          }
          try {
            const date = new Date(apiKey.expiresAt);
            if (Number.isNaN(date.getTime())) {
              return '';
            }
            return date.toISOString().split('T')[0];
          } catch {
            return '';
          }
        })(),
      };

      setFormData({
        ...loadedData,
        tenantId:
          loadedData.tenantId === '' ||
            loadedData.tenantId === null ||
            loadedData.tenantId === undefined
            ? null
            : loadedData.tenantId,
      });
      setOriginalData({
        ...loadedData,
        tenantId:
          loadedData.tenantId === '' ||
            loadedData.tenantId === null ||
            loadedData.tenantId === undefined
            ? null
            : loadedData.tenantId,
        permissions: { ...loadedData.permissions },
      });

      // Load domains for the tenant if set
      if (loadedData.tenantId) {
        loadDomains(Number(loadedData.tenantId));
      }

      // Show duplicate hint notification
      if (duplicateId) {
        showInfo(t('apiKeys.messages.duplicateHint'));
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : t('apiKeys.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [mode, id, duplicateId, tenantSlug, userId, setFormData, setOriginalData, showError, showInfo, t, loadDomains]);

  useEffect(() => {
    loadApiKey();
  }, [mode, id, duplicateId, loadApiKey, setFormData, setOriginalData, setGeneratedApiKey]);

  return {
    tenants,
    loadingTenants,
    loading,
    // Domain scope functionality
    domains,
    loadingDomains,
    loadDomains,
  };
}

